import { useMemo, useRef } from "react";
import {
  IconChevronDown,
  IconChevronUp,
  IconSearch,
  IconSelector,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { Card, DatePickerInput, Group, Input, Loader, Stack, Table, Tbody, Td, Text, Th, Thead, TruncatedTooltip, Tr } from "@/components/ui";
import { parseIso, formatIso } from "@/features/datos-generales/utils/iso-date";
import type { Factura } from "../services/facturas.api";
import {
  filtrarFacturas,
  ordenarFacturas,
  siguienteSort,
  type FacturaFiltros,
  type FacturaSort,
  type FacturaSortKey,
} from "../utils/ordenar-facturas";
import { AccionesDescargaFactura } from "./AccionesDescargaFactura";
import { EstatusFacturaBadge } from "./EstatusFacturaBadge";

const MXN = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2 });

function fecha(iso: string | null): string {
  return iso ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Columnas · el orden y la cantidad deben coincidir EXACTAMENTE con las celdas
 * que renderiza `FacturaRow`. `key` presente → columna ordenable + filtrable
 * (fila de búsqueda bajo los encabezados · mismo patrón que Mis solicitudes);
 * `key: null` → columna informativa sin orden/filtro (Acciones).
 * Columnas de fecha filtran por DÍA con un selector de fecha.
 */
export const COLUMNAS_FACTURAS: {
  key: FacturaSortKey | null;
  label: string;
  align?: "right" | "center";
  w: number;
  filtro?: "fecha";
}[] = [
  // Folio completo de la solicitud · informativa (sin orden/filtro): el folio se arma en
  // el CORE con la sociedad de la solicitud, así que el server no puede filtrar ni ordenar
  // por él; para buscar por número de solicitud está «Núm. documento».
  { key: null, label: "Solicitud", w: 150 },
  { key: "ordenCompra", label: "CA/OC", w: 110 },
  { key: "emisor", label: "Emisor", w: 200 },
  { key: "receptor", label: "Receptor", w: 200 },
  { key: "validacion", label: "Fecha validación", w: 140, filtro: "fecha" },
  { key: "timbrado", label: "Fecha timbrado", w: 140, filtro: "fecha" },
  { key: "emision", label: "Fecha emisión", w: 140, filtro: "fecha" },
  { key: "serie", label: "Serie", w: 80 },
  { key: "folio", label: "Folio", w: 80 },
  { key: "uuid", label: "UUID", w: 165 },
  { key: "total", label: "Total", align: "right", w: 110 },
  { key: "moneda", label: "Moneda", w: 90 },
  { key: "estatus", label: "Estatus", w: 120 },
  { key: null, label: "Acciones", align: "center", w: 120 },
  { key: "tipo", label: "Tipo", w: 70 },
  { key: "observaciones", label: "Observaciones", w: 160 },
  { key: "numDoc", label: "Núm. documento", w: 120 },
];

const COLS = COLUMNAS_FACTURAS;

// Suma exacta de los anchos de columna · con `layout: fixed` la tabla mide esto
// y cada columna respeta su ancho (los textos largos TRUNCAN + tooltip).
export const MIN_WIDTH_FACTURAS = 2195;
const MIN_WIDTH = MIN_WIDTH_FACTURAS;

/**
 * Tabla ancha de "Mis facturas" · MISMO estilo que la tabla de Mis solicitudes:
 * contenedor `Card`, encabezado con fondo gris (`ink-100`), franja de filtros
 * tenue (`foreground/10`), espaciado `lg` y barra de scroll horizontal ESPEJO
 * arriba de la tabla. Orden por columna (click en el encabezado) y filtro de
 * texto/fecha por columna. Filtros y orden viven en el padre y van al SERVER
 * (paginado server-side); aquí además se filtra/ordena en cliente la página
 * visible para feedback instantáneo durante el debounce.
 */
export function FacturasTable({
  facturas,
  filtros,
  sort,
  buscando = false,
  mostrarFiltros = true,
  onFiltrar,
  onOrdenar,
}: {
  facturas: Factura[];
  filtros: FacturaFiltros;
  sort: FacturaSort | null;
  buscando?: boolean;
  /** Fila de filtros por columna colapsable · el toggle vive en la toolbar de la página. */
  mostrarFiltros?: boolean;
  onFiltrar: (key: FacturaSortKey, valor: string) => void;
  onOrdenar: (sort: FacturaSort) => void;
}) {
  const visibles = useMemo(
    () => ordenarFacturas(filtrarFacturas(facturas, filtros), sort),
    [facturas, filtros, sort],
  );

  const alOrdenar = (key: FacturaSortKey) => onOrdenar(siguienteSort(sort, key));

  // Scrollbar horizontal DUPLICADO arriba de la tabla (espejo · mismo patrón que
  // Mis solicitudes): una barra superior sincronizada en ambos sentidos con el
  // contenedor real de la tabla, para no tener que bajar a buscar la nativa.
  const tablaScrollRef = useRef<HTMLDivElement>(null);
  const barraSuperiorRef = useRef<HTMLDivElement>(null);

  const alScrollBarra = () => {
    if (tablaScrollRef.current && barraSuperiorRef.current)
      tablaScrollRef.current.scrollLeft = barraSuperiorRef.current.scrollLeft;
  };
  const alScrollTabla = () => {
    if (tablaScrollRef.current && barraSuperiorRef.current)
      barraSuperiorRef.current.scrollLeft = tablaScrollRef.current.scrollLeft;
  };

  return (
    <Card padding="xs" className="relative p-0">
      {buscando && (
        <Group gap={6} className="pointer-events-none absolute right-3 top-2 z-20">
          <Loader size="xs" color="multiva" />
          <Text size="xs" c="dimmed" fw={500}>
            Buscando…
          </Text>
        </Group>
      )}

      {/* Barra de scroll horizontal ESPEJO arriba de la tabla · su contenido
          interno replica el ancho real de la tabla (por CSS, EN VIVO: `w-full`
          + `min-width: MIN_WIDTH` = max(ancho del contenedor, MIN_WIDTH), igual
          que la tabla `layout:fixed` con `minWidth`). Sin medir por JS se evita
          el lag que hacía aparecer un scrollbar transitorio al animar el sidebar.
          Se sincroniza en ambos sentidos con el contenedor de abajo. */}
      <div
        ref={barraSuperiorRef}
        onScroll={alScrollBarra}
        className="scrollbar-suave h-3 shrink-0 overflow-x-auto overflow-y-hidden"
        aria-hidden="true"
      >
        <div className="h-px w-full" style={{ minWidth: MIN_WIDTH }} />
      </div>

      {/* Altura acotada · el scroll vertical ocurre DENTRO del contenedor para que
          el `thead` sticky se pegue (un `overflow-x-auto` suelto rompe el sticky en
          el scroll de la página). El scroll HORIZONTAL no muestra barra abajo
          (`overflow-x: hidden`) — se conduce SOLO por la barra espejo de arriba,
          que fija `scrollLeft` (funciona aunque overflow-x esté hidden). */}
      <div
        ref={tablaScrollRef}
        onScroll={alScrollTabla}
        className="scrollbar-suave max-h-[calc(100dvh-16rem)] overflow-x-hidden overflow-y-auto"
      >
        <Table
          layout="fixed"
          highlightOnHover
          verticalSpacing="md"
          horizontalSpacing="sm"
          minWidth={MIN_WIDTH}
          className="uppercase"
        >
          {/* Encabezado STICKY con fondo gris opaco (`ink-100`) · al hacer scroll
              vertical se mantiene visible y las filas no se cuelan por detrás. */}
          <Thead className="sticky top-0 z-10 bg-ink-100 dark:bg-ink-800">
            <Tr>
              {COLS.map((c, i) =>
                c.key ? (
                  <ThOrdenable
                    key={c.key}
                    sortKey={c.key}
                    sort={sort}
                    onSort={alOrdenar}
                    align={c.align === "right" ? "right" : "left"}
                    w={c.w}
                  >
                    {c.label}
                  </ThOrdenable>
                ) : (
                  <Th key={`x-${i}`} align={c.align} style={{ width: c.w, verticalAlign: "middle" }}>
                    <Text size="xs" fw={600} className="whitespace-nowrap leading-tight">
                      {c.label}
                    </Text>
                  </Th>
                ),
              )}
            </Tr>
            {/* Franja de filtros tenue · igual que Mis solicitudes · colapsable. */}
            {mostrarFiltros && (
            <Tr className="border-t border-border bg-foreground/10">
              {COLS.map((c, i) => {
                if (!c.key) return <Th key={`xf-${i}`} className="!px-1 !py-1.5" />;
                const key = c.key;
                return (
                  <Th key={key} className="!px-1 !py-1.5 align-top">
                    {c.filtro === "fecha" ? (
                      <DatePickerInput
                        size="xs"
                        clearable
                        placeholder="Fecha…"
                        value={parseIso(filtros[key])}
                        onChange={(d) => onFiltrar(key, formatIso(d))}
                        wrapperClassName="normal-case"
                        placeholderClassName="truncate text-muted-foreground/60"
                      />
                    ) : (
                      <Input
                        size="xs"
                        placeholder={`${c.label}…`}
                        leftSection={<IconSearch size={12} stroke={1.6} />}
                        value={filtros[key] ?? ""}
                        onChange={(e) => onFiltrar(key, e.currentTarget.value)}
                        aria-label={`Filtrar por ${c.label}`}
                        className="normal-case overflow-hidden text-ellipsis whitespace-nowrap placeholder:text-muted-foreground/60"
                      />
                    )}
                  </Th>
                );
              })}
            </Tr>
            )}
          </Thead>
          <Tbody className="transition-opacity duration-120" style={{ opacity: buscando ? 0.5 : 1 }}>
            {visibles.length === 0 ? (
              <Tr>
                <Td colSpan={COLS.length}>
                  <Text size="sm" c="dimmed" ta="center" className="py-3 normal-case">
                    Sin resultados para los filtros.
                  </Text>
                </Td>
              </Tr>
            ) : (
              visibles.map((f) => <FacturaRow key={f.id} f={f} />)
            )}
          </Tbody>
        </Table>
      </div>
    </Card>
  );
}

/** Encabezado clickeable con indicador de orden (mismo patrón que Mis solicitudes). */
function ThOrdenable({
  children,
  sortKey,
  sort,
  onSort,
  align = "left",
  w,
}: {
  children: React.ReactNode;
  sortKey: FacturaSortKey;
  sort: FacturaSort | null;
  onSort: (key: FacturaSortKey) => void;
  align?: "left" | "right";
  w?: number;
}) {
  const activa = sort?.key === sortKey;
  const Icono = activa ? (sort!.dir === "asc" ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <Th className="!p-0 align-middle" style={{ width: w }}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="flex w-full items-center justify-start gap-1 px-2.5 py-2"
        aria-label={`Ordenar por ${typeof children === "string" ? children : sortKey}`}
      >
        <Text size="xs" fw={600} className="whitespace-nowrap uppercase leading-tight text-ink-700 dark:text-ink-200">
          {children}
        </Text>
        <Icono
          size={15}
          stroke={2}
          className={`shrink-0 ${activa ? "text-multiva-600" : "text-ink-600 dark:text-ink-300"}`}
        />
      </button>
    </Th>
  );
}

function FacturaRow({ f }: { f: Factura }) {
  return (
    <Tr>
      <Td>
        <Text size="sm" fw={600} ff="mono" className="whitespace-nowrap" title="Folio de la solicitud de pago">
          {f.folioSolicitud || "—"}
        </Text>
      </Td>
      <Td>
        <Text size="sm" fw={600}>{f.ordenCompraClave ?? "—"}</Text>
      </Td>
      <Td>
        <TruncatedTooltip label={`${f.nombreEmisor} · ${f.rfcEmisor}`} disabled={!f.nombreEmisor}>
          <Stack gap={0} className="min-w-0">
            <Text size="sm" fw={600} truncate>
              {f.nombreEmisor}
            </Text>
            <Text size="sm" c="dimmed" truncate>
              {f.rfcEmisor}
            </Text>
          </Stack>
        </TruncatedTooltip>
      </Td>
      <Td>
        <TruncatedTooltip label={`${f.nombreReceptor} · ${f.rfcReceptor}`} disabled={!f.nombreReceptor}>
          <Stack gap={0} className="min-w-0">
            <Text size="sm" fw={600} truncate>
              {f.nombreReceptor}
            </Text>
            <Text size="sm" c="dimmed" truncate>
              {f.rfcReceptor}
            </Text>
          </Stack>
        </TruncatedTooltip>
      </Td>
      <Td>
        <Text size="sm">{fecha(f.fechaValidacion)}</Text>
      </Td>
      <Td>
        <Text size="sm">{fecha(f.fechaTimbrado)}</Text>
      </Td>
      <Td>
        <Text size="sm">{fecha(f.fechaEmision)}</Text>
      </Td>
      <Td>
        <Text size="sm">{f.serie ?? "—"}</Text>
      </Td>
      <Td>
        <Text size="sm">{f.folio ?? "—"}</Text>
      </Td>
      <Td>
        {/* UUID fiscal · en MAYÚSCULAS por la clase `uppercase` de la tabla ·
            envuelve (`break-all`) para caber en su columna. */}
        <Text size="sm" ff="mono" className="break-all leading-tight">
          {f.uuid}
        </Text>
      </Td>
      <Td align="right">
        <Text size="sm" fw={700} className="whitespace-nowrap">
          {MXN.format(f.total)}
        </Text>
      </Td>
      <Td>
        <Text size="sm" fw={600}>{f.moneda}</Text>
      </Td>
      <Td>
        <EstatusFacturaBadge estatus={f.estatus} />
      </Td>
      <Td>
        {/* Acciones como CHIPS de color (fondo tintado por tipo) · resaltan más
            que los íconos sueltos y siguen siendo distinguibles de un vistazo:
            XML gris/neutro, Factura PDF rojo, Acuse azul. */}
        <AccionesDescargaFactura factura={f} />
      </Td>
      <Td>
        <Text size="sm">{f.tipoComprobante}</Text>
      </Td>
      <Td>
        <TruncatedTooltip label={f.observaciones ?? "—"} disabled={!f.observaciones}>
          <Text size="sm" truncate className="normal-case">
            {f.observaciones ?? "—"}
          </Text>
        </TruncatedTooltip>
      </Td>
      <Td>
        <Text size="sm" title="Número de la solicitud de pago">
          {f.numeroSolicitud}
        </Text>
      </Td>
    </Tr>
  );
}

